import React, { useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";

interface CollectionCenter {
  _id: string;
  collectionCenterName: string;
  address: string;
}

interface CollectionCenterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  className?: string;
  required?: boolean;
  autoSelect?: boolean; // New prop to enable auto-selection logic
  saveToStorage?: boolean; // New prop to enable saving to localStorage
  showAllOption?: boolean; // New prop to show "All Centers" option
}

const CollectionCenterDropdown: React.FC<CollectionCenterDropdownProps> = ({
  value,
  onChange,
  label = "",
  error = false,
  helperText = "",
  disabled = false,
  size = "small",
  fullWidth = true,
  className = "custom-input",
  required = false,
  autoSelect = true, // Default to true
  saveToStorage = false, // Default to false
  showAllOption = false, // Default to false
}) => {
  // Get user profile from Redux store
  const profile = useSelector((state: any) => state.profileSlice?.profile);

  // Extract collection centers from profile
  const collectionCenters: CollectionCenter[] =
    profile?.data?.collectionCenter || [];

  // Auto-selection logic
  useEffect(() => {
    if (!autoSelect || !collectionCenters.length || value) return;

    // First priority: Get from localStorage (skip "all")
    const savedCenterId = localStorage.getItem("selectedCollectionCenterId");
    if (
      savedCenterId &&
      savedCenterId !== "all" &&
      collectionCenters.some(center => center._id === savedCenterId)
    ) {
      onChange(savedCenterId);
      return;
    }

    if (showAllOption) {
      onChange("all");
      return;
    }

    // If only one center, auto-select it
    if (collectionCenters.length === 1) {
      onChange(collectionCenters[0]._id);
      return;
    }

    // If multiple centers and no saved preference, select first one
    if (collectionCenters.length > 1) {
      onChange(collectionCenters[0]._id);
      return;
    }
  }, [collectionCenters, value, onChange, autoSelect, showAllOption]);

  const handleChange = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value;
    onChange(selectedValue);

    // Save to localStorage if saveToStorage is enabled
    if (saveToStorage && selectedValue && selectedValue !== "all") {
      localStorage.setItem("selectedCollectionCenterId", selectedValue);
    }
  };

  const placeholderText = label
    ? `${label}${required ? " *" : ""}`
    : "Collection Center";

  return (
    <div>
      <Tooltip
        title={
          collectionCenters.length === 0
            ? "No collection centers available. Please contact administrator."
            : ""
        }
        placement="top"
        arrow
      >
        <FormControl
          className={className}
          fullWidth={fullWidth}
          size={size}
          error={error}
          disabled={disabled || collectionCenters.length === 0}
        >
          <InputLabel
            id="collection-center-select-label"
            sx={{
              fontSize: size === "small" ? "14px" : "16px",
              color: required && !value ? "error.main" : undefined,
            }}
          >
            {placeholderText}
          </InputLabel>
          <Select
            labelId="collection-center-select-label"
            id="collection-center-select"
            value={value || (showAllOption ? "all" : "")}
            label={placeholderText}
            onChange={handleChange}
            disabled={disabled || collectionCenters.length === 0}
          >
            {showAllOption && (
              <MenuItem value="all">All</MenuItem>
            )}
            {collectionCenters.map(center => (
              <MenuItem key={center._id} value={center._id}>
                {center.collectionCenterName}
              </MenuItem>
            ))}
            {collectionCenters.length === 0 && (
              <MenuItem value="" disabled>
                No Centers Available
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Tooltip>
    </div>
  );
};

export default CollectionCenterDropdown;
